/**
 * GrowthCustomers — Customer segments with visual proportion display (premium design)
 */
import React, { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "sonner";
import { Users, TrendingUp } from "lucide-react";

const segmentConfig = {
  new: {
    label: "Clientes Novos",
    color: "bg-emerald-500",
    bgColor: "bg-emerald-50",
    icon: TrendingUp,
    textColor: "text-emerald-700"
  },
  returning: {
    label: "Clientes Recorrentes",
    color: "bg-blue-500",
    bgColor: "bg-blue-50",
    icon: Users,
    textColor: "text-blue-700"
  }
};

export default function GrowthCustomers({ authAxios, period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        setLoading(true);
        const res = await authAxios.get(`/growth/ceo?period=${period}`);
        const ceoData = res.data;
        setData({
          new_customers: ceoData.new_customers.value,
          returning_customers: ceoData.returning_customers.value,
          total_customers:
            ceoData.new_customers.value + ceoData.returning_customers.value
        });
      } catch (err) {
        console.error("Error fetching customers:", err);
        toast.error("Erro ao carregar clientes");
      } finally {
        setLoading(false);
      }
    };

    fetchCustomers();
  }, [period, authAxios]);

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="grid md:grid-cols-2 gap-4">
          {Array(2).fill(0).map((_, i) => (
            <Card key={i}>
              <CardHeader className="pb-2">
                <Skeleton className="h-4 w-24" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-10 w-16 mb-2" />
                <Skeleton className="h-3 w-20" />
              </CardContent>
            </Card>
          ))}
        </div>
        <Card>
          <CardContent className="pt-6">
            <Skeleton className="h-8 w-full" />
          </CardContent>
        </Card>
      </div>
    );
  }

  if (!data) {
    return (
      <Card className="bg-slate-50 border-slate-200">
        <CardContent className="pt-6">
          <p className="text-muted-foreground">Sem dados de clientes</p>
        </CardContent>
      </Card>
    );
  }

  const segments = [
    {
      key: "new",
      value: data.new_customers,
      ...segmentConfig.new
    },
    {
      key: "returning",
      value: data.returning_customers,
      ...segmentConfig.returning
    }
  ];

  return (
    <div className="space-y-6">
      {/* Header Summary */}
      <Card className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white border-slate-700/50">
        <CardContent className="pt-6">
          <div className="text-center">
            <p className="text-slate-300 text-sm font-semibold uppercase tracking-widest mb-2">Total de Clientes</p>
            <p className="text-5xl font-bold tracking-tight">{data.total_customers}</p>
            <p className="text-slate-400 text-sm mt-3">Neste período</p>
          </div>
        </CardContent>
      </Card>

      {/* Segment Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {segments.map((seg) => {
          const percentage = ((seg.value / data.total_customers) * 100).toFixed(1);
          const Icon = seg.icon;

          return (
            <Card key={seg.key} className={`${seg.bgColor} border-none`}>
              <CardContent className="pt-6">
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div className="flex-1">
                    <p className={`text-xs font-semibold ${seg.textColor} uppercase tracking-widest mb-2`}>
                      {seg.label}
                    </p>
                    <p className="text-4xl font-bold text-slate-900">{seg.value}</p>
                  </div>
                  <div className={`w-12 h-12 rounded-lg ${seg.color} bg-opacity-20 flex items-center justify-center flex-shrink-0`}>
                    <Icon className="w-6 h-6" style={{ color: seg.color.replace('bg-', '').split('-')[0] === 'emerald' ? '#10b981' : '#3b82f6' }} />
                  </div>
                </div>

                {/* Percentage Indicator */}
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-semibold text-slate-600">Percentual do total</span>
                  <span className={`text-sm font-bold ${seg.textColor}`}>{percentage}%</span>
                </div>
                <div className="w-full bg-white bg-opacity-50 rounded-full h-2.5">
                  <div
                    className={`h-2.5 rounded-full transition-all ${seg.color}`}
                    style={{ width: `${percentage}%` }}
                  />
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Proportion Visualization */}
      <Card className="border-slate-200">
        <CardHeader>
          <CardTitle className="text-lg font-bold">Distribuição de Clientes</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex gap-2 h-16 rounded-lg overflow-hidden shadow-sm">
            <div
              className="bg-emerald-500 flex items-center justify-center text-white font-bold text-sm transition-all hover:bg-emerald-600"
              style={{
                flex: data.new_customers,
                minWidth: '40px'
              }}
              title={`Novos: ${data.new_customers}`}
            >
              {data.total_customers > 20 && `${data.new_customers}`}
            </div>
            <div
              className="bg-blue-500 flex items-center justify-center text-white font-bold text-sm transition-all hover:bg-blue-600"
              style={{
                flex: data.returning_customers,
                minWidth: '40px'
              }}
              title={`Recorrentes: ${data.returning_customers}`}
            >
              {data.total_customers > 20 && `${data.returning_customers}`}
            </div>
          </div>
          <div className="flex justify-between mt-4 text-xs text-slate-600">
            <div>
              <p className="font-semibold text-emerald-700">Novos</p>
              <p>{((data.new_customers / data.total_customers) * 100).toFixed(1)}%</p>
            </div>
            <div className="text-right">
              <p className="font-semibold text-blue-700">Recorrentes</p>
              <p>{((data.returning_customers / data.total_customers) * 100).toFixed(1)}%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200">
        <CardContent className="pt-6">
          <p className="text-sm text-blue-900">
            💡 <strong>Dica:</strong> Mantenha um equilíbrio entre clientes novos e recorrentes para estabilidade de receita.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
